import { useCallback, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { isValidId, type GroupId } from 'balus-common';
import type { UserEntity } from '@user/UserEntity';
import { EditUserModal } from '@user/EditUserModal';
import { LanguageSwitchModal } from '@framework/ui/elements/LanguageSwitchModal/LanguageSwitchModal';
import { useDetectGroupId } from '@group/hooks/useDetectGroupId';
import { AdminMenu } from '../AdminMenu';
import { AIUsageMenuItem } from './AIUsageMenuItem';
import { MenuButton } from './MenuButton';
import { MenuLink } from './MenuLink';
import { NameLabel } from './NameLabel';

type Props = {
  userEntity: UserEntity;
  onLogout(): void;
};

/**
 * 通常ユーザ向けのポップアップメニュー
 */
export const RegularUserPopupMenu: React.FC<Props> = ({ userEntity, onLogout }: Props) => {
  const { t } = useTranslation();
  // ユーザ編集モーダルの表示制御
  const [showEditUserModal, setEditUserModal] = useState<boolean>(false);
  const handleOpenEditUserModal = useCallback(() => setEditUserModal(true), []);
  const handleCloseEditUserModal = useCallback(() => setEditUserModal(false), []);

  // 言語切り替えモーダルの表示制御
  const [showLanguageModal, setShowLanguageModal] = useState<boolean>(false);
  const handleOpenLanguageModal = useCallback(() => setShowLanguageModal(true), []);
  const handleCloseLanguageModal = useCallback(() => setShowLanguageModal(false), []);

  const groupId = useDetectGroupId();

  const showAdminMenu = userEntity.isLeviiDomain();

  return (
    <>
      {/* アイコン+名前 */}
      <NameLabel name={userEntity.name} userId={userEntity.id} imageUrl={userEntity.imageUrl} isAnonymous={false} />

      <MenuLink href="https://balus-help.levii.co.jp/" label={t('framework.topNav.help')} />

      <Link to="/" className="block w-full px-4 py-2 text-left text-gray-800 hover:bg-gray-200">
        {t('common.label.backToGroupSelection')}
      </Link>

      {showAdminMenu && <AdminMenu />}

      <MenuButton onClick={handleOpenEditUserModal}>{t('framework.topNav.settings')}</MenuButton>
      <EditUserModal isOpen={showEditUserModal} onClose={handleCloseEditUserModal} userEntity={userEntity} />

      {isValidId(groupId) && <AIUsageMenuItem groupId={groupId as GroupId} />}

      <MenuButton onClick={handleOpenLanguageModal}>{t('framework.language.label')}</MenuButton>
      <LanguageSwitchModal isOpen={showLanguageModal} onClose={handleCloseLanguageModal} />

      <MenuButton onClick={onLogout}>{t('framework.topNav.logout')}</MenuButton>
    </>
  );
};
